import type { Phrase } from '../types';

/**
 * Strings added for the manager and admin panels (venue form, enquiry
 * follow-up, admin filters) plus the venue-page lines that show what the
 * venue form collects. Kept in their own file so the big en.ts stays stable.
 */
export const staffEn = {
  // enquiry stages added with follow-up tracking
  statusFollowUp: 'follow-up',
  statusInterested: 'interested',
  statusBooked: 'booked',

  // enquiries: filters + follow-up
  enqAnyVenue: 'All venues',
  enqReceivedFrom: 'Received from',
  enqReceivedTo: 'Received to',
  enqReceivedOn: 'Received {date}',
  enqSearchHint: 'Name, mobile, email or message',
  enqShownCount: { one: '{count} enquiry', other: '{count} enquiries' },
  enqDueCount: { one: '{count} follow-up due', other: '{count} follow-ups due' },
  enqShowDue: 'Show follow-ups due',
  enqShowAll: 'Show all',
  enqNoMatch: 'No enquiries match these filters.',
  enqFollowUp: 'Follow up',
  enqFollowTitle: 'Follow up · {name}',
  enqFollowUpOn: 'Next follow-up',
  enqFollowUpShort: 'Follow up {date}',
  enqNoFollowUp: 'No date set',
  enqClearFollowUp: 'Clear date',
  enqNotesHistory: 'Notes and call history',
  enqNoNotes: 'No notes yet.',
  enqAddNote: 'Add a note',
  enqAddNoteHint: 'What was discussed, what the guest asked for, what you promised…',
  enqHasNotes: 'Has notes',
  enqFollowSaved: 'Enquiry updated.',

  // admin: filters, sorting
  admSortBy: 'Sort by',
  admSortNewest: 'Newest first',
  admSortOldest: 'Oldest first',
  admSortName: 'Name (A–Z)',
  admSortCapacity: 'Largest capacity',
  admSortRating: 'Highest rated',
  admSortMostBookings: 'Most bookings',
  admSortMostVenues: 'Most venues',
  admSortTopSpend: 'Highest spend',
  admSortHighestPrice: 'Highest price',
  admSortEventDate: 'Event date',
  admSortEventSoonest: 'Event date (soonest)',
  admSortEventLatest: 'Event date (latest)',
  admSortUnhandledFirst: 'Unhandled first',
  admFilteredCount: '{shown} of {total} shown',
  admAnyVerification: 'Any verification',
  admAnyListing: 'Listed or not',
  admAnyPayment: 'Any payment',
  admAnyRole: 'Anyone',
  admAnyEntity: 'Anything',
  admAnyKind: 'Messages and appeals',
  admKind: 'Type',
  admKindContact: 'Contact messages',
  admKindAppeal: 'Suspension appeals',
  admOptPendingVerif: 'Awaiting verification',
  admOptPendingApproval: 'Awaiting approval',
  admOptUnpaid: 'Unpaid',
  admOptPartial: 'Part paid',
  admOptPaid: 'Paid in full',
  admSearchMessages: 'Name, mobile, email, subject or message',
  admSearchEnquiries: 'Guest, mobile, venue or manager',
  admSearchAudit: 'Person, action or record number',
  admNoMessageMatch: 'No messages match these filters.',

  // venue form
  hallMapsUrl: 'Google Maps link',
  hallMapsUrlInvalid: 'Paste a Google Maps link (google.com/maps or maps.app.goo.gl)',
  hallMapsUrlTest: 'Check link',
  hallMapsFilled: 'Address filled from the map link. Please check it.',
  hallNonAcRooms: 'Non-AC rooms',
  hallNonAcRoomCapacity: 'Non-AC rooms sleep (people)',
  hallSecFood: 'Food & dining',
  hallDiningCapacity: 'Dining seating capacity',
  hallDiningAcCapacity: 'AC dining seats',
  hallDiningHint: 'How many guests can eat at once, and how many of those seats are air-conditioned. Enter 0 if there is no dining area.',
  hallDiningAcTooMany: 'Cannot be more than the dining seating capacity',
  hallHourlyPriceWhileOff:
    'Hourly booking is off, so guests cannot book by the hour. Turn it on, or set the hourly price to 0.',
  hallPeakMultiplierEffect: '×{factor} means weekend bookings cost {percent}% more than weekdays.',
  hallPeakMultiplierSame: '1 means weekends cost the same as weekdays. Enter 1.2 to charge 20% more on weekends.',

  // legal documents
  upLicenceNumberLabel: 'Licence / certificate number',
  upLicenceNumberHint: 'e.g. TCR/2021/04567',
  upLicenceAuthorityLabel: 'Issuing authority',
  upLicenceAuthorityHint: 'e.g. Thrissur Municipal Corporation',
  upFileTypes: 'PDF, JPG or PNG · up to 10 MB',

  // venue page: what the form now collects
  vnAcRooms: 'AC rooms',
  vnNonAcRooms: 'Non-AC rooms',
  vnNonAcRoomsVal: { one: '{count} room', other: '{count} rooms' },
  vnNonAcRoomsSleep: '{rooms} · sleeps {people}',
  vnDining: 'Dining area',
  vnDiningVal: 'Seats {count}',
  vnDiningAcVal: 'Seats {count} ({ac} in AC)',

  // venue form: facilities as tick boxes from the admin's list
  hallFacilities: 'Facilities',
  hallFacilityRetired: 'No longer on the list. Untick to remove it from this venue.',
} satisfies Record<string, Phrase>;
